import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Table, 
  Tabs, 
  Row, 
  Col, 
  Space, 
  Typography, 
  Statistic, 
  InputNumber,
  Modal,
  Popconfirm 
} from 'antd';
import { 
  PlusOutlined, 
  SaveOutlined, 
  ClearOutlined, 
  DeleteOutlined, 
  ShopOutlined, 
  UserOutlined, 
  InboxOutlined, 
  SendOutlined,
  HomeOutlined,
  LogoutOutlined,
  SearchOutlined
} from '@ant-design/icons';
import fetchData, { fetchdatanotequal } from '../../functions/fetchData';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../../layout/Layout';
import dayjs from 'dayjs';
import WarehouseTopbar from '../../layout/WarehouseTopbar';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

const StockInOut = () => {
  // Get user type
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('in');
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [billRefNo, setBillRefNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => dayjs());
  const [remarks, setRemarks] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [passNo, setPassNo] = useState('');
  const [category, setCategory] = useState(null);
  const [stockEntries, setStockEntries] = useState([
    {
      key: Date.now(),
      item_id: null,
      itemName: '',
      sponsor_name: '',
      stock_qty: 0,
      unit: 'pcs',
      rate: 0,
      net_amount: 0,
      vat: 7,
      includeVat: true,
      ostock: 0,
      remark: ''
    }
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const itemSelectRefs = useRef({});
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm] = Form.useForm();

  // Effects
  useEffect(() => {
    fetchSuppliers();
    fetchItems();
    fetchCategories();
    fetchNextPONumber();
    fetchStocks();
  }, [activeTab]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        const newKey = Date.now();
        setStockEntries(prev => [
          ...prev,
          {
            key: newKey,
            item_id: null,
            itemName: '',
            stock_qty: 0,
            unit: 'pcs',
            rate: 0,
            net_amount: 0,
            vat: activeTab === 'in' ? 7 : 0,
            includeVat: activeTab === 'in' ? true : false,
            ostock: 0,
            remark: ''
          }
        ]);
        toast.info('New item row added!');
        // Focus on the new row's item select after render
        setTimeout(() => {
          const selectRef = itemSelectRefs.current[newKey];
          if (selectRef) {
            selectRef.focus();
            // Open the dropdown automatically
            if (selectRef.blur) {
              selectRef.focus();
            }
          }
        }, 150);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab]);

  // Data fetching functions
  const fetchSuppliers = async () => {
    try {
      await fetchData('supplier', setSuppliers, 'id', {});
    } catch (error) {
      // console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const itemsData = await fetchData('items', null, 'id', { flag: 1 });
     // console.log('Fetched items from API:', itemsData);
      
      if (!itemsData || !Array.isArray(itemsData)) {
        // console.error('Invalid items data received:', itemsData);
        setItems([]);
        return;
      }
      
      const stockItems = itemsData.map(item => ({
        id: item.id,
        name: item.name || '',
        brand: item.brand || '',
        code: item.code || '',
        unit: item.unit || 'pcs',
        rate: 0,
        avgRate: 0,
        ostock: 0,
        sponsor_name: item.sponsor_name || ''
      }));

      try {
        const wstockData = await fetchData('wstock', null, 'id', {});
        //console.log('Fetched wstock data:', wstockData);

        if (wstockData && Array.isArray(wstockData)) {
          stockItems.forEach(item => {
            // Get all entries for this item name and find the one with highest ID (most recent)
            const itemEntries = wstockData.filter(w => w.name === item.name);
            if (itemEntries.length > 0) {
              // Sort by ID descending and get the first one (highest ID = most recent)
              const latestEntry = itemEntries.sort((a, b) => parseInt(b.id) - parseInt(a.id))[0];
              //console.log(`Latest entry for item "${item.name}":`, latestEntry);

              item.rate = parseFloat(latestEntry.rate || latestEntry.offerprice || latestEntry.mrp || 0);
              // total is the stock quantity; a legitimate 0 must not fall through
              // to "amount", which is a monetary value, not a quantity.
              item.ostock = parseFloat(latestEntry.total ?? 0);
              item.unit = latestEntry.unit || 'pcs';
              // Sponsor name is already set from items table above

              // Average purchase rate - same formula as ClosingStock.jsx's groupStockByItem:
              // average of non-zero rates from purchase entries only (excludes Club/consumption
              // rows, which record a different rate that isn't the purchase cost).
              const purchaseRates = itemEntries
                .filter(w => !(w.sname && w.sname.toLowerCase() === 'club'))
                .map(w => parseFloat(w.rate || 0))
                .filter(rate => rate > 0);
              item.avgRate = purchaseRates.length > 0
                ? purchaseRates.reduce((sum, rate) => sum + rate, 0) / purchaseRates.length
                : 0;
            }
          });
        }
      } catch (err) {
        // console.log('wstock not available, using items only:', err);
      }

     // console.log('Final stock items to display with sponsor names:', stockItems);
      setItems(stockItems);
    } catch (error) {
      // console.error('Failed to fetch items:', error);
      toast.error('Failed to fetch items');
      setItems([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const cats = await fetchData('categories', null, 'id', {});
     // console.log('Fetched categories:', cats);
      setCategories(cats || []);
    } catch (error) {
      // console.error('Failed to fetch categories:', error);
    }
  };

  const fetchNextPONumber = async () => {
    try {
      const data = await fetchData('wstock', null, 'po', {});
      if (data && data.length > 0) {
        const maxPO = Math.max(...data.map(item => parseInt(item.po) || 0));
        setPoNumber((maxPO + 1).toString());
      } else {
        setPoNumber('1');
      }
    } catch (error) {
      setPoNumber('1');
    }
  };

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const allStocks = await fetchData('wstock', null, 'id', {});
      
      // Filter by type on the client side if type field exists
      // Otherwise show all records for the current tab
      const filteredStocks = (allStocks || []).filter(stock => {
        if (activeTab === 'in') {
          // For stock in: show records with type='in' OR sname != 'Club'
          return stock.type === 'in' || (!stock.type && stock.sname !== 'Club');
        } else {
          // For stock out: show records with type='out' OR sname = 'Club'
          return stock.type === 'out' || (!stock.type && stock.sname === 'Club');
        }
      });
      
      setStocks(filteredStocks);
    } catch (error) {
      // console.error('Failed to fetch stock data:', error);
      toast.error('Failed to fetch stock data');
    } finally {
      setLoading(false);
    }
  };

  // Stock entry management
  const addNewStockEntry = useCallback(() => {
    setStockEntries(prev => [
      ...prev,
      {
        key: Date.now(),
        item_id: null,
        itemName: '',
        sponsor_name: '',
        stock_qty: 0,
        unit: 'pcs',
        rate: 0,
        net_amount: 0,
        vat: activeTab === 'in' ? 7 : 0,
        includeVat: activeTab === 'in' ? true : false,
        ostock: 0,
        remark: ''
      }
    ]);
  }, [activeTab]);

  const removeStockEntry = (key) => {
    if (stockEntries.length > 1) {
      setStockEntries(prev => prev.filter(entry => entry.key !== key));
    }
  };

  const handleItemChange = (value, index) => {
    const selectedItem = items.find(item => item.id === value);
    if (!selectedItem) return;

    // console.log('Selected item:', selectedItem);
    // console.log('Sponsor name for selected item:', selectedItem.sponsor_name);

    const newEntries = [...stockEntries];
    newEntries[index] = {
      ...newEntries[index],
      item_id: selectedItem.id,
      itemName: `${selectedItem.brand || 'No Brand'} - ${selectedItem.name}`,
      sponsor_name: selectedItem.sponsor_name || '',
      unit: selectedItem.unit,
      rate: activeTab === 'out' ? selectedItem.avgRate : selectedItem.rate,
      ostock: selectedItem.ostock
    };
    // console.log('Updated entry with sponsor name:', newEntries[index]);
    setStockEntries(newEntries);
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...stockEntries];
    newEntries[index][field] = value;
    
    // Auto-calculate rate when qty or net_amount changes (for stock in only)
    if (activeTab === 'in' && (field === 'stock_qty' || field === 'net_amount')) {
      const qty = field === 'stock_qty' ? (value || 0) : (newEntries[index].stock_qty || 0);
      const netAmount = field === 'net_amount' ? (value || 0) : (newEntries[index].net_amount || 0);
      
      if (qty > 0 && netAmount > 0) {
        // Calculate rate = net_amount / qty (this will be the inclusive rate)
        newEntries[index].rate = parseFloat((netAmount / qty).toFixed(2));
      }
    }
    
    setStockEntries(newEntries);
  };

  // Calculation functions
  const calculateEntryTotals = (entry) => {
    const qty = parseFloat(entry.stock_qty) || 0;
    const rate = parseFloat(entry.rate) || 0;
    const vat = parseFloat(entry.vat) || 0;
    const ostock = parseFloat(entry.ostock) || 0;
    const includeVat = entry.includeVat || false;
    
    let subtotal, vatAmount, netAmount;
    
    if (includeVat && vat > 0) {
      // Rate includes VAT - extract VAT from the rate
      // If rate = 107 (including 7% VAT), then exclusive rate = 107/1.07 = 100
      const vatMultiplier = 1 + (vat / 100);
      const exclusiveRate = rate / vatMultiplier;
      subtotal = qty * exclusiveRate;
      vatAmount = subtotal * (vat / 100);
      netAmount = qty * rate; // Total is simply qty * inclusive rate
    } else {
      // Rate excludes VAT - add VAT on top
      subtotal = qty * rate;
      vatAmount = (subtotal * vat) / 100;
      netAmount = subtotal + vatAmount;
    }
    
    const totalStock = activeTab === 'in' ? ostock + qty : ostock - qty;
    
    return { subtotal, vatAmount, netAmount, totalStock };
  };

  // Form handlers
  const handleSubmit = async () => {
    if (!selectedSupplier) {
      toast.error('Please select a supplier/department');
      return;
    }
    if (!billRefNo.trim()) {
      toast.error('Please enter reference number');
      return;
    }
    if (activeTab === 'in' && !category) {
      toast.error('Please select a category');
      return;
    }
    
    const validEntries = stockEntries.filter(e => e.item_id && e.stock_qty > 0);
    if (validEntries.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }

    try {
      setLoading(true);
      
      // Calculate grand totals
      const grandTotals = validEntries.reduce((acc, entry) => {
        const { subtotal, vatAmount, netAmount } = calculateEntryTotals(entry);
        return {
          subtotal: acc.subtotal + subtotal,
          vatAmount: acc.vatAmount + vatAmount,
          netAmount: acc.netAmount + netAmount
        };
      }, { subtotal: 0, vatAmount: 0, netAmount: 0 });
      
      if (activeTab === 'in') {
        // Stock In - Use /stock-in endpoint
        const stockItems = validEntries.map(entry => {
          const selectedItem = items.find(item => item.id === entry.item_id);
          
          // Calculate individual item totals
          const { vatAmount, netAmount } = calculateEntryTotals(entry);
          
          // Send the actual rate, calculated amounts and includeVat flag
          return {
            code: selectedItem?.code || '',
            name: entry.itemName.split(' - ')[1] || entry.itemName,
            sponsor_name: entry.sponsor_name || '',
            qty: entry.stock_qty,
            vat: entry.vat / 100, // Convert percentage to decimal (7% -> 0.07)
            rate: entry.rate, // Send the actual rate as entered by user
            vatamount: parseFloat(vatAmount.toFixed(2)), // Individual item VAT amount
            amount: parseFloat(netAmount.toFixed(2)), // Individual item total amount (including VAT)
            pass: passNo || '',
            uuid: 0,
            remark: entry.remark || '',
            includeVat: entry.includeVat || false // Backend will use this to determine calculation method
          };
        });

        const stockInData = {
          po: poNumber,
          category: category || '',
          supplier_id: selectedSupplier.value,
          sponsor_name: selectedSupplier.label,
          date: invoiceDate.format('YYYY-MM-DD'),
          sname: selectedSupplier.label,
          ref: billRefNo,
          inv: billRefNo, // Using ref as invoice number
          items: stockItems,
          subtotal: grandTotals.subtotal,
          total_vat: grandTotals.vatAmount,
          grand_total: grandTotals.netAmount,
          ledger_amount: grandTotals.netAmount, // This will be saved to supplier_ledger
          remarks: remarks
        };

        const response = await axios.post('/stock/stock-in', stockInData, getHeaders());
        toast.success(`Stock in saved successfully! ${response.data.totalItems} items processed.`);
      } else {
        // Stock Out - Use /stock-out endpoint
        const items = validEntries.map(entry => ({
          name: entry.itemName.split(' - ')[1] || entry.itemName,
          qty: entry.stock_qty,
          remark: entry.remark || ''
        }));

        const stockOutData = {
          items: items,
          department_id: selectedSupplier.value,
          sname: selectedSupplier.label,
          ref: billRefNo,
          date: invoiceDate.format('YYYY-MM-DD'),
          remarks: remarks
        };

        const response = await axios.post('/stock/stock-out', stockOutData, getHeaders());
        toast.success(`Stock out processed successfully! ${response.data.updatedItems.length} items processed.`);
      }

      handleClearForm();
      fetchStocks();
      fetchItems();
      fetchNextPONumber();
    } catch (err) {
      const errorMsg = err.response?.data?.msg || err.response?.data?.error || `Failed to save stock ${activeTab === 'in' ? 'in' : 'out'} entries.`;
      toast.error(errorMsg);
      // console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    setStockEntries([
      {
        key: Date.now(),
        item_id: null,
        itemName: '',
        sponsor_name: '',
        stock_qty: 0,
        unit: 'pcs',
        rate: 0,
        net_amount: 0,
        vat: activeTab === 'in' ? 7 : 0,
        includeVat: activeTab === 'in' ? true : false,
        ostock: 0,
        remark: ''
      }
    ]);
    setSelectedSupplier(null);
    setBillRefNo('');
    setRemarks('');
    setPassNo('');
    setCategory(null);
  };

  const handleEdit = (record) => {
    // console.log('Edit record:', record);
    // console.log('Categories:', categories);
    
    // Find supplier_id from suppliers array based on supplier name
    const supplier = suppliers.find(s => 
      (s.supplier_name === record.sname || s.name === record.sname)
    );
    
    // Find category name if record.category is an ID
    let categoryValue = record.category;
    if (categories.length > 0) {
      const category = categories.find(c => c.id == record.category);
      if (category) {
        categoryValue = category.cat_name;
      }
    }
    
    setEditingRecord({
      ...record,
      supplier_id: supplier ? supplier.id : null
    });
    
    editForm.setFieldsValue({
      category: categoryValue,
      date: record.date ? dayjs(record.date) : dayjs(),
      sname: record.sname,
      name: record.name,
      pass: record.pass,
      qty: record.qty,
      vat: record.vat,
      rate: record.rate,
      amount: record.amount,
      sold_qty: record.sold_qty || 0,
      ref: record.ref,
      inv: record.inv
    });
    setIsEditModalVisible(true);
  };

  const handleDelete = async (record, deleteType = 'id') => {
    try {
      setLoading(true);
      let url;
      
      // Support three deletion methods based on backend API
      if (deleteType === 'id' && record.id) {
        // Delete by item ID
        url = `/stock/delete-stock-in/item/${record.id}`;
      } else if (deleteType === 'name' && record.name) {
        // Delete by item name
        url = `/stock/delete-stock-in/name/${record.name}`;
      } else if (deleteType === 'po' && record.po) {
        // Delete by PO number
        url = `/delete-stock-in/po/${record.po}`;
      } else {
        // Default: delete by ID if available
        if (record.id) {
          url = `/delete-stock-in/item/${record.id}`;
        } else {
          throw new Error('No valid identifier found for deletion');
        }
      }
      
      // console.log('Delete URL:', url);
      await axios.delete(url, getHeaders());
      toast.success('Stock entry deleted successfully!');
      fetchStocks();
    } catch (error) {
      // console.error('Failed to delete stock entry:', error);
      toast.error(error.response?.data?.msg || 'Failed to delete stock entry');
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingRecord(null);
    editForm.resetFields();
  };

  const handleAmountChange = (value) => {
    if (value) {
      const qty = editForm.getFieldValue('qty') || 1;
      // Rate (Inc. VAT) per item = Total Amount / Quantity
      const rate = parseFloat((value / qty).toFixed(2));
      editForm.setFieldsValue({ rate });
    }
  };

  const handleQtyChange = (value) => {
    if (value && value > 0) {
      const amount = editForm.getFieldValue('amount');
      if (amount) {
        // Recalculate rate when quantity changes
        const rate = parseFloat((amount / value).toFixed(2));
        editForm.setFieldsValue({ rate });
      }
    }
  };

  const handleVatChange = (value) => {
    // VAT change doesn't affect the rate when rate includes VAT
    // Rate (Inc. VAT) = Amount / Quantity
  };

  const handleModalSave = async () => {
    try {
      const values = await editForm.validateFields();
      setLoading(true);

      // Find supplier_id from current form selection
      const selectedSupplier = suppliers.find(s => 
        (s.supplier_name === values.sname || s.name === values.sname)
      );
      
      const supplier_id = selectedSupplier ? selectedSupplier.id : editingRecord.supplier_id;
      
      if (!supplier_id) {
        toast.error('Supplier ID is required. Please select a valid supplier.');
        setLoading(false);
        return;
      }

      const qty = parseFloat(values.qty);
      const vat = parseFloat(values.vat);
      const rateIncVAT = parseFloat(values.rate); // Rate per item including VAT (from form)
      const totalAmount = parseFloat(values.amount); // Total amount from form - use this exactly
      
      // Calculate base rate (excluding VAT) for backend
      // Formula: baseRate = rateIncVAT / (1 + vat)
      const baseRate = parseFloat((rateIncVAT / (1 + vat)).toFixed(2));
      const vatamount = parseFloat((rateIncVAT - baseRate).toFixed(2));
      
      // Use exact form values without recalculation
      const itemTotal = totalAmount; // Use exact amount from form
      const subtotal = parseFloat((qty * baseRate).toFixed(2));
      const total_vat = parseFloat((qty * vatamount).toFixed(2));

      // Prepare update data according to backend API structure
      const updateData = {
        category: values.category,
        sponsor_name: editingRecord.sponsor_name || values.sname,
        date: values.date.format('YYYY-MM-DD'),
        sname: values.sname,
        supplier_id: supplier_id,
        ref: values.ref || '',
        inv: values.inv || '',
        items: [
          {
            id: editingRecord.id, // Include item ID for backend to identify which record to update
            code: editingRecord.code || '',
            name: values.name,
            qty: qty,
            vat: vat,
            rate: baseRate, // Base rate excluding VAT for backend
            vatamount: vatamount, // VAT amount per item
            amount: totalAmount, // Use exact amount from form
            pass: values.pass || '',
            uuid: 0,
            remark: '',
            includeVat: true
          }
        ],
        subtotal: subtotal,
        total_vat: total_vat,
        grand_total: itemTotal,
        ledger_amount: itemTotal,
        remarks: ''
      };

      // console.log('Update payload:', updateData);
      // console.log('Updating PO:', editingRecord.po);

      await axios.put(`/stock/update-stock-in/${editingRecord.po}`, updateData, getHeaders());
      toast.success('Stock entry updated successfully!');
      setIsEditModalVisible(false);
      setEditingRecord(null);
      editForm.resetFields();
      fetchStocks();
    } catch (error) {
      // console.error('Failed to update stock entry:', error);
      toast.error(error.response?.data?.msg || error.message || 'Failed to update stock entry');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    handleClearForm();
    setCurrentPage(1);
  };

  // Table column definitions
  const stockEntryColumns = [
    {
      title: 'Item',
      dataIndex: 'item_id',
      key: 'item_id',
      width: 250,
      render: (value, record, index) => {
      //  console.log('Rendering dropdown - Items available:', items.length, 'Current value:', value);
        return (
          <Select
            ref={(el) => (itemSelectRefs.current[record.key] = el)}
            showSearch
            allowClear
            placeholder="Select item..."
            value={value}
            onChange={(val) => handleItemChange(val, index)}
            filterOption={(input, option) => {
              if (!input) return true;
              const children = option?.children;
              if (!children) return false;
              const searchText = typeof children === 'string' ? children : String(children);
              return searchText.toLowerCase().includes(input.toLowerCase());
            }}
            style={{ width: '100%' }}
            notFoundContent={items.length === 0 ? "Loading items..." : "No items found"}
          >
            {items.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name || 'Unnamed'}
              </Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Sponsor Name',
      dataIndex: 'sponsor_name',
      key: 'sponsor_name',
      width: 150,
      render: (value) => <Text>{value || '—'}</Text>
    },
    {
      title: 'Qty',
      dataIndex: 'stock_qty',
      key: 'stock_qty',
      width: 100,
      render: (value, record, index) => (
        <InputNumber
          min={0}
          value={value}
          onChange={(val) => handleEntryChange(index, 'stock_qty', val || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      render: (value, record, index) => (
        <Input
          value={value}
          onChange={(e) => handleEntryChange(index, 'unit', e.target.value)}
        />
      )
    },
    ...(activeTab === 'in'
      ? [
          {
            title: 'Net Amount',
            dataIndex: 'net_amount',
            key: 'net_amount',
            width: 120,
            render: (value, record, index) => (
              <InputNumber
                min={0}
                step={0.01}
                value={value}
                onChange={(val) => handleEntryChange(index, 'net_amount', val || 0)}
                style={{ width: '100%' }}
                placeholder="Enter amount"
              />
            )
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 100,
            render: (value, record, index) => (
              <InputNumber
                min={0}
                step={0.01}
                value={value}
                onChange={(val) => handleEntryChange(index, 'rate', val || 0)}
                style={{ width: '100%' }}
              />
            )
          }
        ]
      : [
          {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 100,
            render: (value, record, index) => (
              <InputNumber
                min={0}
                step={0.01}
                value={value}
                onChange={(val) => handleEntryChange(index, 'rate', val || 0)}
                style={{ width: '100%' }}
              />
            )
          }
        ]),
    {
      title: 'Opening',
      dataIndex: 'ostock',
      key: 'ostock',
      width: 100,
      render: (value) => <Text>{parseFloat(value || 0).toFixed(2)}</Text>
    },
    ...(activeTab === 'in'
      ? [
          {
            title: 'VAT%',
            dataIndex: 'vat',
            key: 'vat',
            width: 80,
            render: (value, record, index) => (
              <InputNumber
                min={0}
                max={100}
                value={value}
                onChange={(val) => handleEntryChange(index, 'vat', val || 0)}
                style={{ width: '100%' }}
              />
            )
          },
          {
            title: 'Include VAT',
            dataIndex: 'includeVat',
            key: 'includeVat',
            width: 100,
            render: (value, record, index) => (
              <input
                type="checkbox"
                checked={value || false}
                onChange={(e) => handleEntryChange(index, 'includeVat', e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
            )
          }
        ]
      : []),
    {
      title: activeTab === 'in' ? 'Calculated Total' : 'Total Value',
      key: 'netAmount',
      width: 120,
      render: (_, record) => {
        // For stock in with net_amount field, use it directly to avoid rounding discrepancies
        const displayAmount = activeTab === 'in' && record.net_amount 
          ? parseFloat(record.net_amount) 
          : calculateEntryTotals(record).netAmount;
        return (
          <Text strong style={{ color: activeTab === 'in' ? '#52c41a' : '#ff4d4f' }}>
            ฿{displayAmount.toFixed(2)}
          </Text>
        );
      }
    },
    {
      title: 'Total Stock',
      key: 'totalStock',
      width: 120,
      render: (_, record) => {
        const { totalStock } = calculateEntryTotals(record);
        return <Text style={{ color: '#1890ff' }}>{totalStock.toFixed(2)}</Text>;
      }
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      render: (value, record, index) => (
        <Input
          placeholder="Add notes..."
          value={value}
          onChange={(e) => handleEntryChange(index, 'remark', e.target.value)}
        />
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) =>
        stockEntries.length > 1 && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeStockEntry(record.key)}
          />
        )
    }
  ];

  // Render helper functions
  const renderStats = () => {
    const today = new Date().toDateString();
    const todayStockIn = stocks
      .filter(s => s.type === 'in' && new Date(s.date).toDateString() === today)
      .reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
    const todayStockOut = stocks
      .filter(s => s.type === 'out' && new Date(s.date).toDateString() === today)
      .reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);

    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Entries"
              value={stocks.length}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Today Stock In"
              value={todayStockIn.toFixed(2)}
              prefix="฿"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Today Stock Out"
              value={todayStockOut.toFixed(2)}
              prefix="฿"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Net Value Today"
              value={(todayStockIn - todayStockOut).toFixed(2)}
              prefix="฿"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderForm = () => (
    <Card
      title={
        <Space>
          {activeTab === 'in' ? <ShopOutlined /> : <UserOutlined />}
          <span>
            {activeTab === 'in'
              ? 'Supplier & Bill Information'
              : 'Department & Stock Out Information'}
          </span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Ctrl+Enter: Add Row
          </Text>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={activeTab === 'in' ? 'Select Supplier *' : 'Department/Issue To *'}
              required
            >
              <Select
                showSearch
                placeholder={
                  activeTab === 'in' ? 'Choose supplier...' : 'Choose department...'
                }
                value={selectedSupplier?.value}
                onChange={(value) => {
                  const supplier = suppliers.find(s => s.id === value);
                  setSelectedSupplier(
                    supplier
                      ? {
                          value: supplier.id,
                          label:
                            supplier.supplier_name ||
                            supplier.name ||
                            `Supplier ${supplier.id}`
                        }
                      : null
                  );
                }}
                filterOption={(input, option) => {
                  const children = option.children;
                  const searchText = typeof children === 'string' ? children : String(children);
                  return searchText.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {suppliers.map(s => (
                  <Option key={s.id} value={s.id}>
                    {s.supplier_name || s.name || `Supplier ${s.id}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Reference No. *" required>
              <Input
                placeholder="Enter ref number..."
                value={billRefNo}
                onChange={(e) => setBillRefNo(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label={activeTab === 'in' ? 'Invoice Date' : 'Issue Date'}>
              <DatePicker
                style={{ width: '100%' }}
                value={invoiceDate}
                onChange={(date) => setInvoiceDate(date || dayjs())}
                format="YYYY-MM-DD"
                inputReadOnly={true}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label={activeTab === 'in' ? 'PO Number' : 'Issue Number'}>
              <Input
                value={poNumber}
                readOnly
                placeholder="Auto Generated"
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label={activeTab === 'in' ? 'Pass No.' : 'Requested By'}>
              <Input
                placeholder={
                  activeTab === 'in' ? 'Pass number...' : 'Requester name...'
                }
                value={passNo}
                onChange={(e) => setPassNo(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Category *" required>
              <Select
                placeholder="Select Category"
                value={category}
                onChange={setCategory}
              >
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.cat_name}>
                    {cat.cat_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={activeTab === 'in' ? 'Remarks' : 'Purpose/Remarks'}>
              <TextArea
                rows={3}
                placeholder={
                  activeTab === 'in'
                    ? 'Enter any remarks or notes...'
                    : 'Enter purpose of issue or remarks...'
                }
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  const renderStockEntryTable = () => {
    // Calculate grand totals
    const grandTotals = stockEntries.reduce((acc, entry) => {
      // For stock in with net_amount, use it directly; otherwise calculate
      const entryNetAmount = activeTab === 'in' && entry.net_amount 
        ? parseFloat(entry.net_amount) 
        : calculateEntryTotals(entry).netAmount;
      
      const { subtotal, vatAmount } = calculateEntryTotals(entry);
      
      return {
        subtotal: acc.subtotal + subtotal,
        vatAmount: acc.vatAmount + vatAmount,
        netAmount: acc.netAmount + entryNetAmount
      };
    }, { subtotal: 0, vatAmount: 0, netAmount: 0 });

    return (
      <Card
        title={`Stock ${activeTab === 'in' ? 'In' : 'Out'} Entries`}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addNewStockEntry}
          >
            Add Item
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          dataSource={stockEntries}
          columns={stockEntryColumns}
          pagination={false}
          scroll={{ x: 1200 }}
          rowKey="key"
        />
        <div style={{ marginTop: 16, padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: 4 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Subtotal"
                value={grandTotals.subtotal.toFixed(2)}
                prefix="฿"
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
            {activeTab === 'in' && (
              <Col xs={24} sm={8}>
                <Statistic
                  title="Total VAT"
                  value={grandTotals.vatAmount.toFixed(2)}
                  prefix="฿"
                  valueStyle={{ fontSize: 18, color: '#ff9800' }}
                />
              </Col>
            )}
            <Col xs={24} sm={8}>
              <Statistic
                title="Grand Total"
                value={grandTotals.netAmount.toFixed(2)}
                prefix="฿"
                valueStyle={{ fontSize: 20, color: '#1890ff', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
        </div>
      </Card>
    );
  };

  const renderActions = () => (
    <Card style={{ marginBottom: 24 }}>
      <Space size="middle" wrap>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={addNewStockEntry}
        >
          Add Entry
        </Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSubmit}
          loading={loading}
          disabled={!selectedSupplier || !billRefNo.trim()}
          style={{
            backgroundColor: activeTab === 'in' ? '#1890ff' : '#faad14'
          }}
        >
          Save Stock {activeTab === 'in' ? 'In' : 'Out'}
        </Button>
        <Button icon={<ClearOutlined />} onClick={handleClearForm}>
          Clear Form
        </Button>
      </Space>
    </Card>
  );

  const renderStockHistory = () => {
    const normalizedSearchTerm = String(searchTerm || '').toLowerCase();
    const filteredStocks = stocks.filter(s => {
      const nameText = String(s?.name ?? '').toLowerCase();
      const supplierText = String(s?.sname ?? '').toLowerCase();
      const poText = String(s?.po ?? '').toLowerCase();

      return (
        nameText.includes(normalizedSearchTerm) ||
        supplierText.includes(normalizedSearchTerm) ||
        poText.includes(normalizedSearchTerm)
      );
    });

    const columns = [
      { 
        title: 'PO', 
        dataIndex: 'po', 
        key: 'po',
        width: 100,
        sorter: (a, b) => (a.po || '').localeCompare(b.po || '')
      },
      { 
        title: 'Category', 
        dataIndex: 'category', 
        key: 'category',
        width: 120,
        sorter: (a, b) => (a.category || '').localeCompare(b.category || '')
      },
      { 
        title: 'Date', 
        dataIndex: 'date', 
        key: 'date',
        width: 110,
        sorter: (a, b) => new Date(a.date) - new Date(b.date)
      },
      {
        title: 'Sname',
        dataIndex: 'sname',
        key: 'sname',
        width: 150,
        sorter: (a, b) => (a.sname || '').localeCompare(b.sname || '')
      },
      { 
        title: 'Name', 
        dataIndex: 'name', 
        key: 'name',
        width: 180,
        sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
      },
      { 
        title: 'Pass', 
        dataIndex: 'pass', 
        key: 'pass',
        width: 100
      },
      { 
        title: 'OStock', 
        dataIndex: 'ostock', 
        key: 'ostock',
        width: 100,
        sorter: (a, b) => parseFloat(a.ostock || 0) - parseFloat(b.ostock || 0),
        render: val => parseFloat(val || 0).toFixed(2)
      },
      {
        title: 'Qty',
        dataIndex: 'qty',
        key: 'qty',
        width: 90,
        sorter: (a, b) => parseFloat(a.qty || 0) - parseFloat(b.qty || 0),
        render: val => parseFloat(val || 0).toFixed(2)
      },
      {
        title: 'VAT',
        dataIndex: 'vat',
        key: 'vat',
        width: 80,
        sorter: (a, b) => parseFloat(a.vat || 0) - parseFloat(b.vat || 0),
        render: val => parseFloat(val || 0).toFixed(2)
      },
      {
        title: 'Rate',
        dataIndex: 'rate',
        key: 'rate',
        width: 100,
        sorter: (a, b) => parseFloat(a.rate || 0) - parseFloat(b.rate || 0),
        render: val => parseFloat(val || 0).toFixed(2)
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 110,
        sorter: (a, b) => parseFloat(a.amount || 0) - parseFloat(b.amount || 0),
        render: val => parseFloat(val || 0).toFixed(2)
      },
      {
        title: 'Sold Qty',
        dataIndex: 'sold_qty',
        key: 'sold_qty',
        width: 100,
        sorter: (a, b) => parseFloat(a.sold_qty || 0) - parseFloat(b.sold_qty || 0),
        render: val => parseFloat(val || 0).toFixed(2)
      },
      {
        title: 'Action',
        key: 'action',
        width: 140,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            <Button
              type="link"
              icon={<SaveOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              title="Edit"
            />
            <Popconfirm
              title="Delete Item"
              description={`Are you sure you want to delete item ${record.name}?`}
              onConfirm={() => handleDelete(record, 'id')}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="Delete Item"
              />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <Card title={`Stock ${activeTab === 'in' ? 'In' : 'Out'} History (Last 500 Records - ${filteredStocks.length} shown)`}>
        <Input
          placeholder="Search by item name, supplier, or PO..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{ marginBottom: 16, width: 400 }}
        />
        <Table
          dataSource={filteredStocks}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 2000 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredStocks.length,
            onChange: setCurrentPage,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
      </Card>
    );
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Logged out successfully!');
    navigate('/login');
  };

  // Main render
  const content = (
    <div style={{ padding: '24px 15px' }}>
      {(userType === 'wstoremanager' || userType === 'warehousestoremanager') && (
        <WarehouseTopbar
          title="Stock In/Out Management"
          subtitle="Warehouse Inventory Management System"
          icon={<InboxOutlined />}
        />
      )}
      <Title level={4} style={{ marginBottom: 24 }}>
        Stock In/Out Management
      </Title>
      {renderStats()}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'in',
            label: (
              <span>
                <SendOutlined rotate={-90} /> Stock In
              </span>
            ),
            children: (
              <>
                {renderForm()}
                {renderStockEntryTable()}
                {renderActions()}
                {renderStockHistory()}
              </>
            )
          },
          {
            key: 'out',
            label: (
              <span>
                <SendOutlined rotate={90} /> Stock Out
              </span>
            ),
            children: (
              <>
                {renderForm()}
                {renderStockEntryTable()}
                {renderActions()}
                {renderStockHistory()}
              </>
            )
          }
        ]}
      />
      
      {/* Edit Modal */}
      <Modal
        title={`Edit Stock Entry - PO: ${editingRecord?.po}`}
        open={isEditModalVisible}
        onOk={handleModalSave}
        onCancel={handleModalCancel}
        width={800}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={loading}
      >
        <Form
          form={editForm}
          layout="vertical"
          initialValues={{
            category: '',
            date: dayjs(),
            sname: '',
            name: '',
            pass: '',
            qty: 0,
            vat: 0.07,
            rate: 0,
            amount: 0,
            sold_qty: 0,
            ref: '',
            inv: ''
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select
                  showSearch
                  placeholder="Select category"
                  filterOption={(input, option) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.cat_name}>
                      {cat.cat_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Supplier Name"
                name="sname"
                rules={[{ required: true, message: 'Please select supplier name' }]}
              >
                <Select
                  showSearch
                  placeholder="Select supplier"
                  filterOption={(input, option) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.supplier_name || supplier.name}>
                      {supplier.supplier_name || supplier.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Item Name"
                name="name"
                rules={[{ required: true, message: 'Please select item name' }]}
              >
                <Select
                  showSearch
                  placeholder="Select item"
                  filterOption={(input, option) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {items.map(item => (
                    <Option key={item.id} value={item.name}>
                      {item.brand ? `${item.brand} - ${item.name}` : item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Pass"
                name="pass"
              >
                <Input placeholder="Pass number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Quantity"
                name="qty"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="Enter quantity"
                  onChange={handleQtyChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="VAT"
                name="vat"
                rules={[{ required: true, message: 'Please enter VAT' }]}
                tooltip="VAT as decimal (e.g., 0.07 for 7%)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={1}
                  step={0.01}
                  placeholder="Enter VAT (e.g., 0.07)"
                  onChange={handleVatChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Rate (Inc. VAT)"
                name="rate"
                tooltip="Per item rate including VAT - Auto-calculated as Amount / Quantity"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Auto-calculated"
                  disabled
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Amount (Inc. VAT)"
                name="amount"
                rules={[{ required: true, message: 'Please enter amount' }]}
                tooltip="Total amount for all items including VAT - Rate will be calculated as Amount/Quantity"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Enter total amount"
                  onChange={handleAmountChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Sold Qty"
                name="sold_qty"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Sold quantity"
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Reference"
                name="ref"
              >
                <Input placeholder="Reference number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Invoice"
                name="inv"
              >
                <Input placeholder="Invoice number" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </div>
  );

  return userType === 'wstoremanager' || userType === 'warehousestoremanager' ? (
    <Layout>
      <>
        {/* Floating Home Button */}
        <Button
          type="primary"
          icon={<HomeOutlined />}
          shape="circle"
          size="large"
          style={{ 
            position: 'fixed',
            right: 24, 
            bottom: 100,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            backgroundColor: '#8B4513',
            borderColor: '#8B4513'
          }}
          onClick={() => navigate('/dashboard/warehousestoremanager')}
          title="Back to Dashboard"
        />
        
        {/* Floating Logout Button */}
        <Button
          type="primary"
          icon={<LogoutOutlined />}
          shape="circle"
          size="large"
          danger
          style={{ 
            position: 'fixed',
            right: 24, 
            bottom: 24,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000
          }}
          onClick={handleLogout}
          title="Logout"
        />
        {content}
      </>
    </Layout>
  ) : (
    <Layout>{content}</Layout>
  );
};

export default StockInOut;
